import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  loginAdmin,
  loginBoothIncharge,
  loginOrgAdmin,
  loginSupport,
} from "@/lib/api";

const roleMeta = {
  admin: {
    title: "Platform Admin Login",
    subtitle: "Use secure super admin credentials",
    identifierLabel: "Username",
    identifierPlaceholder: "AavedakAdmin",
    loginFn: loginAdmin,
    mapPayload: (identifier, password) => ({ username: identifier, password }),
  },
  support: {
    title: "Support Login",
    subtitle: "Operational support and monitoring",
    identifierLabel: "Support Email",
    identifierPlaceholder: "support@aavedak.com",
    loginFn: loginSupport,
    mapPayload: (identifier, password) => ({ identifier, password }),
  },
  org_admin: {
    title: "Organization Admin Login",
    subtitle: "Login with OrgID",
    identifierLabel: "Org ID",
    identifierPlaceholder: "ORG0001",
    loginFn: loginOrgAdmin,
    mapPayload: (identifier, password) => ({ identifier, password }),
  },
  booth_incharge: {
    title: "Booth Incharge Login",
    subtitle: "Login with Booth ID",
    identifierLabel: "Booth ID",
    identifierPlaceholder: "BTH000001",
    loginFn: loginBoothIncharge,
    mapPayload: (identifier, password) => ({ identifier, password }),
  },
};

export const RoleLoginPage = ({ role, onLogin }) => {
  const [identifier, setIdentifier] = useState(role === "admin" ? "AavedakAdmin" : "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const meta = roleMeta[role] || roleMeta.support;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!identifier.trim() || !password.trim()) {
      toast.error("Please fill login credentials");
      return;
    }

    setLoading(true);
    try {
      const payload = meta.mapPayload(identifier.trim(), password.trim());
      const data = await meta.loginFn(payload);
      onLogin({
        token: data.token,
        role: data.role,
        user_id: data.user_id,
        identifier: data.identifier,
        must_change_password: data.must_change_password,
      });
      toast.success("Login successful");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-bg" data-testid={`role-login-page-${role}`}>
      <div className="auth-card" data-testid={`role-login-card-${role}`}>
        <h1 className="auth-title" data-testid={`role-login-title-${role}`}>{meta.title}</h1>
        <p className="auth-subtitle" data-testid={`role-login-subtitle-${role}`}>{meta.subtitle}</p>

        <form className="auth-form" onSubmit={handleSubmit} data-testid={`role-login-form-${role}`}>
          <label>{meta.identifierLabel}</label>
          <Input
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            placeholder={meta.identifierPlaceholder}
            className="auth-input"
            data-testid={`role-login-identifier-${role}`}
          />

          <label>Password</label>
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter password"
            className="auth-input"
            data-testid={`role-login-password-${role}`}
          />

          <Button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
            data-testid={`role-login-submit-${role}`}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
      </div>
    </div>
  );
};
